import { resolveColumns } from '../columns/resolveColumns';

/** How many columns a detail grid is divided into, matching the other surfaces. */
export const DETAIL_COLUMNS = 12;

/** The space between two regions, in pixels, down the page and across it. */
export const DETAIL_GAP = 12;

/**
 * How many rows of a detail layout stand in one page. A region's `h` is counted in rows.
 *
 * A design constant, and a contract every saved layout is authored against: fifteen rows is a page.
 * Changing it moves the fold under every layout already saved, so it is as fixed as the column count.
 */
export const DETAIL_PAGE_ROWS = 15;

/**
 * The shortest a row is allowed to stand, in pixels, however short the page.
 *
 * Below this the rows stop sharing the page out and the page scrolls instead, so a small screen
 * loses the proportion rather than the legibility.
 */
export const DETAIL_MIN_ROW_HEIGHT = 40;

/**
 * How tall a row stands on a page of a given height: its share of a page's worth of rows.
 *
 * This is an invariant of the surface: **a layout taller than a page gives the page a scrollbar, and
 * nothing on it changes size.** A region placed below the fifteenth row is what adds that scrollbar,
 * which is what the editor's line says it does.
 *
 * The divisor is `DETAIL_PAGE_ROWS`, never the layout's own height. Dividing by what the layout
 * happens to be tall is the bug this surface has had twice: it shrinks every region as one is
 * stretched, and it leaves the fold unreachable because the layout always squeezes to fit above it.
 * Nothing here may read a region.
 *
 * @param {Number} height - The page's height in pixels, or nothing while it is unmeasured.
 *
 * @returns {Number}
 */
export const detailRowHeight = (height) =>
  height > 0
    ? Math.max(
        DETAIL_MIN_ROW_HEIGHT,
        // Whole pixels, rounded down. A page's worth of rows at the exact share comes to the page
        // height to the last decimal, and the grid is asked to be at least as tall as the box it
        // sits in - so a fraction over, from the division or from the browser's own rounding, is a
        // scrollbar on a layout that fits. Rounding down costs at most a row's fraction of a pixel
        // and can never overflow.
        Math.floor(
          (height - DETAIL_GAP * (DETAIL_PAGE_ROWS - 1)) / DETAIL_PAGE_ROWS
        )
      )
    : DETAIL_MIN_ROW_HEIGHT;

/** How many rows a layout is tall. */
export const detailRows = (regions) =>
  Math.max(...(regions ?? []).map(({ y = 0, h = 6 }) => y + h), 1);

/** Where a region sits when it declares nothing: a full-width row after everything else. */
const DEFAULT_PLACEMENT = { x: 0, y: 0, w: DETAIL_COLUMNS, h: 6 };

/**
 * Compiles a region's entries, and the entries of any that hold their own.
 *
 * A compiled column carries what a cell needs; the declaration also carries what the widget reads
 * of the entry - where it sits, how its caption is drawn - so both are kept.
 *
 * @param {Array} entries - The entries to compile.
 * @param {Object} options - Compilation options, as `resolveColumns` takes them.
 *
 * @returns {Array}
 */
export const resolveEntries = (entries, options) =>
  (entries ?? []).map((entry) => ({
    ...entry,
    ...resolveColumns([entry], options)[0],
    ...(entry.entries
      ? { entries: resolveEntries(entry.entries, options) }
      : {}),
  }));

/**
 * Compiles a detail layout into the regions the renderer draws.
 *
 * A region keeps its placement and its widget, and its entries are compiled exactly as a list's
 * columns are - same cells, same vocabularies - so a key drawn in a list reads the same on a detail
 * page. Children are compiled the same way, however deep they nest.
 *
 * @param {Array} layout - The regions to show.
 * @param {Object} options - Compilation options, as `resolveColumns` takes them.
 * @param {Object} options.context - Runtime values a saved layout cannot hold, read by cells.
 * @param {Object} options.renderers - Cells to add to the registry, or override in it.
 *
 * @returns {Array}
 */
export const resolveDetail = (layout, { context = {}, renderers = {} } = {}) =>
  (layout ?? []).map((region) => ({
    ...DEFAULT_PLACEMENT,
    ...region,
    entries: resolveEntries(region.entries, { context, renderers }),
    regions: region.regions
      ? resolveDetail(region.regions, { context, renderers })
      : [],
  }));

export default resolveDetail;
